import React, { useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
//import payakornResults from "./payakorn_results.json"; // <- ตรวจ path ให้ตรงโปรเจกต์
import { payakornResults } from "./payakorn_results";

// แผนที่ตัวอักษร -> ค่าตัวเลข (ไทย/อังกฤษ)
const chaldeanMap = {
  // ไทย
  ก: 1,
  ข: 2,
  ฃ: 0,
  ค: 4,
  ฅ: 0,
  ฆ: 3,
  ง: 2,
  จ: 6,
  ฉ: 5,
  ช: 2,
  ซ: 7,
  ฌ: 5,
  ญ: 4,
  ฎ: 5,
  ฏ: 9,
  ฐ: 9,
  ฑ: 3,
  ฒ: 3,
  ณ: 5,
  ด: 1,
  ต: 3,
  ถ: 1,
  ท: 1,
  ธ: 4,
  น: 5,
  บ: 2,
  ป: 2,
  ผ: 8,
  ฝ: 8,
  พ: 8,
  ฟ: 8,
  ภ: 1,
  ม: 5,
  ย: 8,
  ร: 4,
  ล: 6,
  ว: 6,
  ศ: 7,
  ษ: 4,
  ส: 7,
  ห: 5,
  ฬ: 5,
  อ: 6,
  ฮ: 5,
  า: 1,
  ำ: 1,
  ฤ: 1,
  "่": 1,
  "ุ": 1,
  เ: 2,
  แ: 2,
  "้": 2,
  "ู": 2,
  "+": 3,
  ะ: 4,
  โ: 4,
  "ั": 4,
  "ิ": 4,
  "ึ": 5,
  ใ: 6,
  "ี": 7,
  "ื": 7,
  "๊": 7,
  "็": 8,
  ไ: 9,
  "์": 9,
  "ๅ": 9,

  // อังกฤษ (A-Z)
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  E: 5,
  F: 8,
  G: 3,
  H: 5,
  I: 1,
  J: 1,
  K: 2,
  L: 3,
  M: 4,
  N: 5,
  O: 7,
  P: 8,
  Q: 1,
  R: 2,
  S: 3,
  T: 4,
  U: 6,
  V: 6,
  W: 6,
  X: 5,
  Y: 1,
  Z: 7,

  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  0: 0,

};

function normalizeName(raw) {
  // ตัดช่องว่าง/เว้นบรรทัด และแปลงอังกฤษเป็นตัวใหญ่
  return (raw || "").replace(/\s+/g, "").toUpperCase();
}

function charValue(ch) {
  return chaldeanMap[ch] ?? 0; // ถ้าไม่รู้จักตัวอักษรให้เป็น 0
}

function sumDigits(n) {
  return String(Math.abs(n))
    .split("")
    .reduce((acc, d) => acc + Number(d), 0);
}

/**
 * คำนวณเลขกำลังของชื่อ:
 * - รวมค่าตัวอักษรทั้งหมด
 * - ถ้า "ผลรวม > 100" ให้แยกหลักมารวมใหม่ จนกว่าจะไม่เกิน 100 (ตามสเป็ก >100)
 * หมายเหตุ: หากต้องการบังคับช่วง 0-99 ให้เปลี่ยนเงื่อนไข while เป็น ">= 100" ได้
 */
function computeStrengthAndBreakdown(rawName) {
  const name = normalizeName(rawName);
  const breakdown = [];
  let baseSum = 0;

  for (const ch of name) {
    const val = charValue(ch);
    breakdown.push({ ch, val });
    baseSum += val;
  }

  let strength = baseSum;
  while (strength > 100) {
    strength = sumDigits(strength);
  }

  return { name, baseSum, strength, breakdown };
}

// การ์ดหัวข้อทั่วไป (กรอบทองโค้ง + พื้นหลังเข้มไล่เฉด)
const SectionCard = ({ title, children }) => (
  <div
    className="rounded-[18px] p-[1.5px] text-left"
    style={{ background: "linear-gradient(90deg,#FFD86E 0%, #CF7B00 100%)" }}
  >
    <section
      className="rounded-[16px] px-8 py-4"
      style={{ background: "linear-gradient(180deg,#0E1732 0%, #0B122A 100%)" }}
    >
      {title && (
        <h3 className="text-[#FFD15A] font-extrabold text-[15px] mb-2">
          {title}
        </h3>
      )}
      {children}
    </section>
  </div>
);

export default function LuckyNameResultPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyName = decodeURIComponent(searchParams.get("companyName") || "");

  const { name, baseSum, strength, breakdown, meaning, resultTag } =
    useMemo(() => {
      const calc = computeStrengthAndBreakdown(companyName);
      const found = payakornResults.find(
        (x) => String(x.strength) === String(calc.strength)
      );
      return {
        ...calc,
        meaning: found?.text || "ไม่พบคำทำนายสำหรับเลขนี้ในฐานข้อมูล",
        resultTag: found?.Result || null, // <-- ใช้ Result จากฐานข้อมูล
      };
    }, [companyName]);

        // ✅ เปิดเพจแล้วสกรอลไปบนสุดเสมอ
        useEffect(() => {
          if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual"; // ปิด auto-restore ของเบราว์เซอร์
          }
          // ใช้ rAF ให้ทำหลัง DOM วาดเสร็จ
          const id = requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
          });
          return () => cancelAnimationFrame(id);
        }, []);
    
        

  // ✅ Push Event ไป GTM DataLayer หลังได้ผลลัพธ์
  useEffect(() => {
    if (!name) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "click_view_lucky_name_result",
      category: "Lucky_Name",
      name: name,
      baseSum: baseSum,
      strength: strength,
      resultTag: resultTag,
    });
  }, [name]);

  // กำหนดสีพื้นหลังจาก Result
  const summaryStyle =
    resultTag === "Good"
      ? "linear-gradient(135deg, #28A745 0%, #0A3D2E 100%)" // เขียว
      : resultTag === "Avoid"
      ? "linear-gradient(135deg, #A30000 0%, #2B0A15 100%)" // แดง
      : undefined; // Okay = ไม่ต้องใส่สี

  return (
    <div className="min-h-screen bg-[#0A0F28] text-white">
      {/* Back */}
      <header
        className="relative z-10"
        style={{
          position: "absolute",
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 10px)",
        }}
      >
        <div className="w-full h-20 px-5 py-2 flex items-end">
          <button
            aria-label="ย้อนกลับ"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-white/10 hover:bg-white/15 active:bg-white/20 shadow-[0_4px_14px_rgba(0,0,0,0.35)] backdrop-blur-[2px] focus:outline-none focus:ring-2 focus:ring-white/30"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 19L8 12L15 5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="px-8 pt-[100px] pb-12 space-y-4">
        {/* ผลสรุป */}
        <div className="rounded-[18px] p-[1.5px] bg-white/20 shadow-[0_10px_24px_rgba(0,0,0,0.45)]">
          <section
            className="rounded-[16px] px-5 py-4 relative"
            style={{ background: summaryStyle }}
          >
            <div className="absolute inset-0 rounded-[16px] ring-1 ring-white/30 pointer-events-none" />
            <h2 className="text-[#FFD15A] font-bold text-[20px]">
              ผลการตรวจสอบชื่อมงคล
            </h2>
            <p className="text-white/90 text-[18px] mt-1">
              ชื่อ : <span className="font-semibold">{companyName || "-"}</span>
            </p>
            {resultTag && (
              <p className="text-white/80 text-[13px] mt-1">
                ผลประเมิน: <b>{resultTag}</b>
              </p>
            )}
          </section>
        </div>

        {/* คะแนนรวม (เลขกำลัง) */}
        <SectionCard>
          <div className="rounded-[14px] p-4 text-center">
            <div className="text-[#FFD15A] text-[14px] mb-1">
              เลขกำลังจากชื่อ
            </div>
            <div className="text-[#FFD86E] text-[28px] leading-none">
              {strength}
            </div>
          </div>
        </SectionCard>

        {/* คำทำนาย */}
        <SectionCard title={`คำทำนายหมายเลข ${strength}`}>
          <p className="text-[14px] leading-6 text-[#FFD15A] whitespace-pre-line">
            {meaning}
          </p>
        </SectionCard>

        {/* รายละเอียดการคำนวณ */}
        <SectionCard title="รายละเอียดการคำนวณ">
          <div className="text-[#FFD15A] text-[14px] space-y-2">
            <div className="flex flex-wrap gap-2">
              {breakdown.map((b, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 rounded-md bg-white/5 border border-white/10"
                >
                  {b.ch} = {b.val}
                </span>
              ))}
            </div>
            <div className="opacity-80">
              ผลรวมตัวอักษรทั้งหมด:{" "}
              <span className="font-semibold">{baseSum}</span>
            </div>
            {baseSum > 100 && (
              <div className="opacity-80">
                (เกิน 100 จึงแยกหลักมารวมใหม่ → {strength})
              </div>
            )}
          </div>
        </SectionCard>

        {/* Disclaimer */}
        <p className="text-[11px] text-white/50 text-center mt-2">
          ผลการวิเคราะห์เป็นความเชื่อส่วนบุคคล โปรดใช้วิจารณญาณ
        </p>
      </main>
    </div>
  );
}
